import { InternalRefetchQueriesInclude, useMutation } from "@apollo/client";
import { countBy } from "@lyfegen-platform/utils";
import { Box } from "@material-ui/core";
import { DocumentNode } from "graphql";
import { useEffect, useReducer, useState } from "react";
import { useSetRecoilState } from "recoil";
import { filterStateFamily } from "../../store/lyfe-data-table";
import { FilterDialog } from "./FilterDialog";
import {
	calculateSelectedListOptionCount,
	ChipFilterState,
	dashboardInvoiceTimePeriod,
	dashboardTimePeriod,
	DateFilterState,
	FilterListStateType,
	FilterStateType,
	getFilterValues,
	getWhereExpression,
	initiallyShownOptionsCount,
	isFilterEmpty,
	ListFilterOption,
	ListFilterState,
	loadMorePortionSize,
	NumericFilterState,
	sortFilterOptionsIfNeeded,
	TableFilterAction,
} from "./models";
import { PermanentFilters } from "./PermanentFilters";
import { dataModuleString } from "@lyfegen-platform/react-common";

function updateFilterStateByPath(
	state: FilterListStateType,
	path: string,
	update: (filterState: FilterStateType) => FilterStateType
): FilterListStateType {
	const items = path.split("|");
	if (items.length === 1) {
		return state.map((filterState) => (filterState.field === path ? update(filterState) : filterState));
	}

	const setSelectedValueCount = (filterState: ListFilterState): ListFilterState => ({
		...filterState,
		selectedValueCount: calculateSelectedListOptionCount(filterState),
	});

	return state.map((filterState) =>
		filterState.field === items[0]
			? setSelectedValueCount({
					...filterState,
					options: (filterState as ListFilterState).options.map((option) =>
						option.value === items[1] ? { ...option, nested: update(option.nested!) } : option
					),
			  } as ListFilterState)
			: filterState
	);
}

function processSearch(term: string, state: ListFilterState): ListFilterState {
	return {
		...state,
		searchTerm: term,
		options: state.options.map((value) => ({
			...value,
			isFilteredOut: !!term && !value.label.toLowerCase().includes(term.toLowerCase()),
		})),
		shownItemCount: undefined,
	};
}

function processClearFilter(state: FilterStateType): FilterStateType {
	switch (state.selectionType) {
		case "list":
			return {
				...state,
				options: state.options.map((option) => ({
					...option,
					isSelected: false,
					nested: option.nested && {
						...option.nested,
						isEmpty: true,
						selectedValueCount: 0,
						options: option.nested.options.map((nestedOption) => ({ ...nestedOption, isSelected: false })),
					},
				})),
				selectedValueCount: 0,
				isEmpty: true,
			};
		case "date-range":
			return {
				...state,
				startValue: null,
				endValue: null,
				rangeType: "custom",
			};
		case "numeric-range":
			return {
				...state,
				startValue: state.min,
				endValue: state.max,
			};
		case "chip":
			return {
				...state,
				isActive: false,
			};
		default:
			return state;
	}
}

function processSelectAll(state: FilterStateType): FilterStateType {
	if (state.selectionType === "list" && state.isMulti) {
		const updatedOptions = state.options.map((option) => ({ ...option, isSelected: !option.isFilteredOut }));

		return {
			...state,
			options: updatedOptions,
			selectedValueCount: countBy(updatedOptions, (o) => o.isSelected, true),
			isEmpty: updatedOptions.length === 0,
		};
	}

	return { ...state };
}

function processSelection<T>(value: string, state: ListFilterState<T>): ListFilterState<T> {
	const options: Array<ListFilterOption> = state.options.map((option) => ({
		...option,
		isSelected: value === option.value ? !option.isSelected : !!option.isSelected && !!state.isMulti,
		nested:
			state.isMulti && value !== option.value
				? option.nested
				: option.nested && (processClearFilter(option.nested) as ListFilterState),
	}));

	const newState = sortFilterOptionsIfNeeded({
		...state,
		options,
		isEmpty: options.every((option) => !option.isSelected),
	});

	return {
		...newState,
		selectedValueCount: calculateSelectedListOptionCount(newState),
	};
}

function processSetValues(
	state: NumericFilterState | DateFilterState,
	startValue: Date | number | null | undefined,
	endValue: Date | number | null | undefined
) {
	return {
		...state,
		startValue,
		endValue,
	} as NumericFilterState | DateFilterState;
}

function processSetBoolean(value: boolean, state: ChipFilterState) {
	return {
		...state,
		isActive: value,
	} as ChipFilterState;
}

export function filterReducer(state: FilterListStateType, action: TableFilterAction): FilterListStateType {
	switch (action.type) {
		case "search-list-value":
			return updateFilterStateByPath(state, action.path, (state) => processSearch(action.term, state as ListFilterState));

		case "select-list-value":
			return updateFilterStateByPath(state, action.path, (state) => processSelection(action.value, state as ListFilterState));

		case "set-boolean-value":
			return updateFilterStateByPath(state, action.path, (state) => processSetBoolean(action.value, state as ChipFilterState));

		case "clear-selection":
			return updateFilterStateByPath(state, action.path, (state) => processClearFilter(state));

		case "select-current-scope":
			return updateFilterStateByPath(state, action.path, (state) => processSelectAll(state));

		case "set-values":
			return updateFilterStateByPath(state, action.path, (state) =>
				processSetValues(state as NumericFilterState | DateFilterState, action.startValue, action.endValue)
			);

		case "load-more":
			return updateFilterStateByPath(state, action.path, (state) => ({
				...state,
				shownItemCount:
					(state as ListFilterState<any>).shownItemCount === undefined
						? initiallyShownOptionsCount + loadMorePortionSize
						: (state as ListFilterState<any>).shownItemCount! + loadMorePortionSize,
			}));

		case "show-less":
			return updateFilterStateByPath(state, action.path, (state) => ({ ...state, shownItemCount: undefined }));

		case "set-range-type":
			return updateFilterStateByPath(state, action.path, (state) => ({
				...state,
				rangeType: action.rangeType,
				startValue: null,
				endValue: null,
			}));

		case "clear-all":
			return state.map(processClearFilter);

		case "restore-state":
			return action.state;

		default:
			return state;
	}
}

function persistFilters(state: FilterListStateType, mutate: (options: { variables: any }) => void, previousStates: any) {
	const newPersistedFilters = state
		.filter((filter) => !isFilterEmpty(filter))
		.reduce(
			(a, filter) => ({
				...a,
				[filter.field]: getFilterValues(filter),
			}),
			{
				[dashboardTimePeriod]: previousStates?.[dashboardTimePeriod],
				[dashboardInvoiceTimePeriod]: previousStates?.[dashboardInvoiceTimePeriod],
			} as any
		);
	mutate({ variables: { req: JSON.stringify(newPersistedFilters) } });
}

interface IProps {
	title: string;
	initialState: FilterListStateType;
	saveFilterQuery: DocumentNode;
	countQuery: DocumentNode;
	module?: string;
	filterPersistedState?: any;
	refetchQueries?: InternalRefetchQueriesInclude;
	restoreState?: boolean;
}

export function FiltersRoot(props: IProps) {
	const [state, dispatcher] = useReducer(filterReducer, props.initialState);
	const [mutate] = useMutation(props.saveFilterQuery, {
		refetchQueries: props.refetchQueries,
		context: { module: props.module ?? dataModuleString },
	});
	const setFilterState = useSetRecoilState(filterStateFamily(props.title));
	const [isDialogOpen, setOpenDialog] = useState(false);

	function handleSave() {
		persistFilters(state, mutate, props.filterPersistedState);
		setFilterState(getWhereExpression(state));
	}

	useEffect(() => {
		if (!isDialogOpen) {
			handleSave();
		}
	}, [state]);

	useEffect(() => {
		if (props.restoreState && !isDialogOpen) {
			dispatcher({ type: "restore-state", state: props.initialState });
		}
	}, [props.initialState]);

	useEffect(() => {
		if (JSON.stringify(props.initialState) !== JSON.stringify(state)) {
			dispatcher({ type: "restore-state", state: props.initialState });
		}
	}, [JSON.stringify(props.initialState)]);

	useEffect(() => {
		const storageDomainFilter = sessionStorage.getItem("domain");
		if (storageDomainFilter) {
			dispatcher({
				type: "select-list-value",
				value: storageDomainFilter,
				path: "domainName",
			});
			sessionStorage.removeItem("domain");
		}
	}, []);

	return (
		<Box display="flex" alignItems="center" justifyContent="space-between" flexGrow={1}>
			<PermanentFilters state={state} dispatcher={dispatcher} />
			<FilterDialog
				state={state}
				countQuery={props.countQuery}
				module={props.module}
				dispatcher={dispatcher}
				isDialogOpen={isDialogOpen}
				setOpenDialog={setOpenDialog}
				handleApply={handleSave}
				title={props.title}
			/>
		</Box>
	);
}
